import { Link } from "react-router-dom";
import { ArrowRight, Mail, MapPin, Quote, Wrench } from "lucide-react";
import Reveal from "@/components/Reveal";
import { TEAM, TESTIMONIAL, SITE } from "@/data/site";

const About = () => {
  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden bg-[#001529]">
        <img src="/images/locations.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#00203d]/85 to-[#00203d]/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 lg:pt-12 lg:pb-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-500 mb-4">About VBS</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight max-w-3xl">
              Three decades of modular expertise
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              Our team of professionals has over thirty years of experience in the modular industry and is
              committed to providing outstanding client service and the highest quality buildings to all projects.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pt-10 pb-16 sm:pt-12 sm:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" aria-labelledby="story-heading">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600 mb-4">Who We Are</p>
            <h2 id="story-heading" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 gold-underline">
              A customer driven organization
            </h2>
            <div className="mt-8 space-y-4 text-base text-slate-600 leading-relaxed">
              <p>
                Vision Building Systems, L.L.C. (VBS) is a Florida limited liability company serving the
                Southeastern United States in the sale, delivery, and installation of modular buildings.
              </p>
              <p>
                Our innovative management team is committed to satisfying your unique construction and space
                needs. VBS' areas of expertise cover all modular applications including commercial, religious,
                educational, hotel and health care.
              </p>
              <p>
                Vision Building Systems offers full project services including design, budget proposal, turn-key
                construction, and project management, on any project from small and simple to large and complex.
              </p>
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <img src="/images/hero-2.jpg" alt="Permanent modular building installation" className="rounded-xl object-cover h-64 w-full shadow-lg" />
              <img src="/images/gs-18.jpg" alt="Completed ground set modular building" className="rounded-xl object-cover h-64 w-full shadow-lg mt-8" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white border-y border-slate-100" aria-labelledby="team-heading">
        <div className="pt-10 pb-16 sm:pt-12 sm:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600 mb-4">Meet the Team</p>
            <h2 id="team-heading" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 gold-underline">
              Leadership you can build on
            </h2>
          </Reveal>
          <div className="mt-12 space-y-10">
            {TEAM.map((member, i) => (
              <Reveal key={member.name} delay={80}>
                <article
                  data-testid={`team-member-${member.name.toLowerCase().replace(/[\s.]+/g, "-")}`}
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-slate-50 rounded-2xl border border-slate-100 overflow-hidden ${
                    i % 2 === 1 ? "" : ""
                  }`}
                >
                  <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                    <img src={member.img} alt={`Project completed under ${member.name}`} className="h-72 w-full object-cover" />
                  </div>
                  <div className="lg:col-span-7 p-6 sm:p-10">
                    <h3 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">{member.name}</h3>
                    <p className="text-sm font-bold text-amber-600 uppercase tracking-[0.14em] mt-1">{member.role}</p>
                    <p className="mt-5 text-sm text-slate-600 leading-relaxed">{member.bio}</p>
                    <a
                      href={`mailto:${member.email}`}
                      data-testid={`team-email-${i}`}
                      className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#00203d] hover:text-amber-600 transition-colors duration-200"
                    >
                      <Mail className="h-4 w-4" aria-hidden="true" /> {member.email}
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pt-10 pb-16 sm:pt-12 sm:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" aria-labelledby="work-locations-heading">
        <h2 id="work-locations-heading" className="sr-only">Our Work and Locations</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal>
            <article data-testid="about-work-card" className="overflow-hidden rounded-xl bg-white border border-slate-100 shadow-sm">
              <img src="/images/work-cover.jpg" alt="VBS sales center project" className="h-64 w-full object-cover" />
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-2.5">
                  <Wrench className="h-5 w-5 text-amber-600" aria-hidden="true" />
                  <h3 className="font-heading text-xl font-bold text-slate-900">Our Work</h3>
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  We work with our clients to create unique floor plans with custom options that meet local code
                  requirements. Once a contract is completed, we work directly with the manufacturer throughout
                  the build, and closely manage delivery and set up.
                </p>
              </div>
            </article>
          </Reveal>
          <Reveal delay={120}>
            <article data-testid="about-locations-card" className="overflow-hidden rounded-xl bg-white border border-slate-100 shadow-sm">
              <img src="/images/locations.jpg" alt="Modular building delivery in the Southeast" className="h-64 w-full object-cover" />
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-2.5">
                  <MapPin className="h-5 w-5 text-amber-600" aria-hidden="true" />
                  <h3 className="font-heading text-xl font-bold text-slate-900">Locations</h3>
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  {SITE.serviceArea}. We provide custom modular buildings to our customers across the entire
                  Southeastern United States.
                </p>
              </div>
            </article>
          </Reveal>
        </div>

        <Reveal delay={100}>
          <figure data-testid="about-testimonial" className="mt-16 text-center max-w-4xl mx-auto">
            <Quote className="h-10 w-10 text-amber-500 mx-auto" aria-hidden="true" />
            <blockquote className="mt-6 font-heading text-xl sm:text-2xl font-semibold text-slate-800 leading-relaxed">
              "{TESTIMONIAL.quote}"
            </blockquote>
            <figcaption className="mt-6">
              <p className="font-bold text-[#00203d]">{TESTIMONIAL.author}</p>
              <p className="text-sm text-slate-500">Happy Clients — our number 1 priority</p>
            </figcaption>
          </figure>
        </Reveal>

        <Reveal delay={150}>
          <div className="mt-14 text-center">
            <Link
              to="/contact"
              data-testid="about-contact-button"
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-bold px-8 py-4 rounded-md transition-colors duration-200"
            >
              Work with us <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default About;
